import { useContext } from 'react';

import type { ReactNode } from 'react';
import { useMatches } from 'react-router';

import { PageTitleContext } from '@/shared/pageTitle/context';
import type { RouteHandle } from '@/shared/pageTitle/RouteHandle';

const FALLBACK_TITLE = 'GX';

const isRouteHandle = (handle: unknown): handle is RouteHandle =>
  typeof handle === 'object' && handle !== null && 'title' in handle;

export type RouteMeta = { title: ReactNode; section?: ReactNode };

/**
 * Resolves the current route's topbar breadcrumb. Precedence: a page-set
 * override, then the deepest route `handle` (calling `title(params)` when it is a
 * resolver), then a fallback title.
 */
export const useRouteMeta = (): RouteMeta => {
  const matches = useMatches();
  const ctx = useContext(PageTitleContext);
  const override = ctx?.override ?? null;

  const match = [...matches].toReversed().find(entry => isRouteHandle(entry.handle));
  const handle = match?.handle as RouteHandle | undefined;

  let handleTitle: ReactNode = undefined;
  if (handle) {
    handleTitle =
      typeof handle.title === 'function' ? handle.title(match?.params ?? {}) : handle.title;
  }

  const title = override?.title ?? handleTitle ?? FALLBACK_TITLE;

  // SECTION SEAM: today `section` comes from the route handle (or a page
  // override). When nav grouping lands, derive it from the active nav group
  // here — no other file needs to change.
  const section = override?.section ?? handle?.section;

  return { title, section };
};
